/**
 * Number-syntax repair. When the top reading of the written characters isn't
 * a valid answer ("3-4", "1.", "--2"), find the most likely reading that is,
 * using each character's runner-up candidates.
 *
 * The pad only ever *suggests* this reading (the player confirms it with a
 * second tap on ✓) — it is never applied silently, because a quietly changed
 * answer that turns out wrong would cost the player a brick layer.
 */
import { parseAnswer } from '@dropstack/core';
import type { HwCandidate, HwChar } from '../../contracts.ts';

export const DECODE = {
  /** Alternatives scoring below this are never proposed (the top candidate always is). */
  MIN_ALT_SCORE: 0.12,
  /** Partial readings kept per position. */
  BEAM_WIDTH: 32,
} as const;

/**
 * The answer grammar  -? ( digits ( "." digits | "/" digits )? | "." digits )
 * as a tiny state machine, so impossible prefixes are pruned early.
 */
type GrammarState = 'start' | 'sign' | 'int' | 'point' | 'decimals' | 'slash' | 'denominator';

const ACCEPTING: ReadonlySet<GrammarState> = new Set<GrammarState>(['int', 'decimals', 'denominator']);

const isDigit = (c: HwChar): boolean => c >= '0' && c <= '9';

function nextState(s: GrammarState, c: HwChar): GrammarState | null {
  const digit = isDigit(c);
  switch (s) {
    case 'start':
      return c === '-' ? 'sign' : digit ? 'int' : c === '.' ? 'point' : null;
    case 'sign':
      return digit ? 'int' : c === '.' ? 'point' : null;
    case 'int':
      return digit ? 'int' : c === '.' ? 'point' : c === '/' ? 'slash' : null;
    case 'point':
    case 'decimals':
      return digit ? 'decimals' : null;
    case 'slash':
    case 'denominator':
      return digit ? 'denominator' : null;
  }
}

interface Hypothesis {
  state: GrammarState;
  picks: number[];
  logScore: number;
}

/**
 * Best-scoring choice of one candidate per character whose text is a valid
 * answer (checked with core parseAnswer, so "3/0" is rejected too).
 *
 * @param lists  candidates per character, best first
 * @param fixed  per character, a candidate index the player already chose
 *               (kept as is), or null to let the decoder choose
 * @returns candidate indices, or null when no valid reading exists
 */
export function bestValidPicks(
  lists: readonly (readonly HwCandidate[])[],
  fixed: readonly (number | null)[] = [],
): number[] | null {
  if (!lists.length) return null;
  let beam: Hypothesis[] = [{ state: 'start', picks: [], logScore: 0 }];
  lists.forEach((candidates, i) => {
    const pinned = fixed[i];
    const options = candidates
      .map((c, k) => ({ c, k }))
      .filter(({ c, k }) => (pinned != null ? k === pinned : k === 0 || c.score >= DECODE.MIN_ALT_SCORE));
    const next: Hypothesis[] = [];
    for (const h of beam) {
      for (const { c, k } of options) {
        const state = nextState(h.state, c.char);
        if (state) next.push({ state, picks: [...h.picks, k], logScore: h.logScore + Math.log(Math.max(c.score, 1e-6)) });
      }
    }
    beam = next.sort((a, b) => b.logScore - a.logScore).slice(0, DECODE.BEAM_WIDTH);
  });
  for (const h of beam) {
    if (!ACCEPTING.has(h.state)) continue;
    const text = h.picks.map((k, i) => lists[i]![k]!.char).join('');
    if (parseAnswer(text) !== null) return h.picks;
  }
  return null;
}
